// src/pages/builder/TreasureMapPagesPanel.js
//
// Layout Builder → 🗺️ Treasure map pages. Five pages are built in (desert,
// forest, snow, mountains, jungle). Add more pages here; they join the walk
// after the built-in ones, and the chests are split across all of them.
//
// For each page:
//   1. a picture — upload one, or paste a link from the Image Studio
//      (tip: ask for "a hand-drawn treasure map page, same style, with a
//      dotted trail from the left edge to the right edge through a …")
//   2. the trail — click along the dotted path on the picture, from where
//      you walk IN to where you walk OUT (Undo / Clear to fix)
//   3. a name for the land ("🏜️ Desert", "💧 Waterfall valley") and how many
//      chests fit along it
// Saved to treasureMapPages/{id}; every map in every class uses them, in order.
//
// HOLIDAY VERSIONS: pick "Holiday version of" (one of the 5 built-in pages)
// and a season — that picture replaces the built-in page while the season is
// on sale (Layout Builder ▸ Season dates), and is hidden the rest of the year.
// "Copy its trail" reuses the built-in page's trail when the picture is the
// same scene redrawn (snowy forest, spooky desert…).

import React, { useEffect, useRef, useState } from "react";
import { addDoc, collection, deleteDoc, doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { getDownloadURL, getStorage, ref as storageRef, uploadBytes } from "firebase/storage";
import { db } from "../../firebase/firebaseConfig";
import { BUILT_IN_PAGES, chestSlots, pageCapacity } from "../../components/treasure/mapPages";
import { SEASONS, seasonOf } from "../../components/avatar/seasons/seasonData";

const blank = () => ({ name: "", emoji: "🗺️", imageUrl: "", aspect: 16 / 9, trail: [], capacity: 7, order: Date.now(), season: "", replaces: "" });

const TrailEditor = ({ page, onChange }) => {
  const imgRef = useRef(null);
  const add = (e) => {
    const r = imgRef.current.getBoundingClientRect();
    const x = +(((e.clientX - r.left) / r.width) * 100).toFixed(2);
    const y = +(((e.clientY - r.top) / r.height) * 100).toFixed(2);
    onChange({ trail: [...(page.trail || []), [x, y]] });
  };
  const pts = page.trail || [];
  return (
    <div>
      <div style={{ position: "relative", width: "100%", maxWidth: 900, cursor: "crosshair", borderRadius: 14, overflow: "hidden", boxShadow: "0 0 0 3px #c08a3e" }} onClick={add}>
        <img
          ref={imgRef}
          src={page.imageUrl}
          alt=""
          draggable="false"
          style={{ width: "100%", display: "block" }}
          onLoad={(e) => {
            const im = e.currentTarget;
            if (im.naturalWidth && im.naturalHeight) onChange({ aspect: im.naturalWidth / im.naturalHeight }, true);
          }}
        />
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
          <polyline points={pts.map((p) => p.join(",")).join(" ")} fill="none" stroke="#e11d48" strokeWidth="0.6" strokeDasharray="1.5 1" vectorEffect="non-scaling-stroke" />
        </svg>
        {pts.map((p, i) => (
          <span
            key={i}
            style={{
              position: "absolute", left: `${p[0]}%`, top: `${p[1]}%`, width: 12, height: 12, marginLeft: -6, marginTop: -6, borderRadius: "50%",
              background: i === 0 ? "#16a34a" : i === pts.length - 1 ? "#e11d48" : "#fff", border: "2px solid #7c2d12", pointerEvents: "none",
            }}
          />
        ))}
      </div>
      <p className="lb-hint" style={{ margin: "6px 0" }}>
        Click along the trail from where you walk <b style={{ color: "#16a34a" }}>in</b> to where you walk <b style={{ color: "#e11d48" }}>out</b> — a point every bend. {pts.length} points.
      </p>
      <button type="button" className="lb-btn lb-btn-soft" onClick={() => onChange({ trail: pts.slice(0, -1) })} disabled={!pts.length}>
        ↶ Undo point
      </button>{" "}
      <button type="button" className="lb-btn lb-btn-soft" onClick={() => onChange({ trail: [] })} disabled={!pts.length}>
        Clear trail
      </button>
    </div>
  );
};

const TreasureMapPagesPanel = () => {
  const [open, setOpen] = useState(false);
  const [pages, setPages] = useState([]);
  const [edit, setEdit] = useState(null); // { id?, …page }
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    return onSnapshot(
      collection(db, "treasureMapPages"),
      (snap) => setPages(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))),
      () => setPages([])
    );
  }, [open]);

  const set = (patch) => setEdit((e) => ({ ...e, ...patch }));

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setMsg(null);
    try {
      const path = `topic-images/treasure-map/page-${Date.now()}.${(file.name.split(".").pop() || "jpg").toLowerCase()}`;
      const r = storageRef(getStorage(), path);
      await uploadBytes(r, file, { contentType: file.type || "image/jpeg", cacheControl: "public, max-age=31536000" });
      set({ imageUrl: await getDownloadURL(r), trail: [] });
    } catch (e) {
      setMsg({ tone: "bad", text: e?.message || "Upload failed." });
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!edit.imageUrl) return setMsg({ tone: "bad", text: "Add a picture first." });
    if ((edit.trail || []).length < 2) return setMsg({ tone: "bad", text: "Click at least two points along the trail." });
    if (edit.replaces && !edit.season) return setMsg({ tone: "bad", text: "Pick which season this holiday version is for." });
    setBusy(true);
    const { id, ...data } = edit;
    const out = {
      name: String(data.name || "").trim() || "Map page",
      imageUrl: data.imageUrl,
      aspect: Number(data.aspect) || 16 / 9,
      trail: (data.trail || []).map(([x, y]) => ({ x, y })), // Firestore can't store nested arrays
      capacity: Math.max(2, Math.min(12, Math.floor(Number(data.capacity) || 7))),
      regions: [{ to: 1, name: String(data.name || "").replace(/^\S+\s/, "") || "Map", emoji: data.emoji || "🗺️" }],
      order: Number(data.order) || Date.now(),
      active: data.active !== false,
      // a holiday version of a built-in page (both or neither)
      season: data.season && data.replaces ? data.season : "",
      replaces: data.season && data.replaces ? data.replaces : "",
      updatedAt: serverTimestamp(),
    };
    try {
      if (id) await setDoc(doc(db, "treasureMapPages", id), out, { merge: true });
      else await addDoc(collection(db, "treasureMapPages"), out);
      setEdit(null);
      setMsg({ tone: "ok", text: "Saved — the maps use it straight away." });
    } catch (e) {
      setMsg({ tone: "bad", text: e?.message || "Couldn't save." });
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const move = async (p, dir) => {
    const i = pages.findIndex((x) => x.id === p.id);
    const other = pages[i + dir];
    if (!other) return;
    await Promise.all([
      setDoc(doc(db, "treasureMapPages", p.id), { order: other.order }, { merge: true }),
      setDoc(doc(db, "treasureMapPages", other.id), { order: p.order }, { merge: true }),
    ]);
  };

  return (
    <section className="lb-card">
      <div className="lb-card-head">
        <span className="lb-step">🗺️</span>
        <div>
          <h2>Treasure map pages</h2>
          <p className="lb-hint">The walking treasure map. The 5 lands below are built in, and each class's chests are split across them. Pages you add join the end of the walk — or make a holiday version of a built-in land.</p>
        </div>
        <button type="button" className="lb-btn" onClick={() => setOpen((v) => !v)}>
          {open ? "Hide" : "Open"}
        </button>
      </div>

      {open && (
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {BUILT_IN_PAGES.map((p, i) => (
              <figure key={p.id} style={{ margin: 0, width: 220 }}>
                <img src={p.imageUrl} alt="" style={{ width: "100%", borderRadius: 10 }} />
                <figcaption className="lb-hint">
                  Page {i + 1} · {p.regions?.[0]?.emoji} {p.name} (built in · fits {pageCapacity(p)} chests, spaced out)
                </figcaption>
              </figure>
            ))}
            {pages.map((p, i) => (
              <figure key={p.id} style={{ margin: 0, width: 220, opacity: p.active === false ? 0.5 : 1 }}>
                <img src={p.imageUrl} alt="" style={{ width: "100%", borderRadius: 10 }} />
                <figcaption className="lb-hint">
                  {p.season && p.replaces
                    ? `${seasonOf(p.season)?.emoji || "🎉"} Holiday: ${p.name} — replaces ${BUILT_IN_PAGES.find((b) => b.id === p.replaces)?.name || "a page"} during ${seasonOf(p.season)?.name || p.season}`
                    : `Page ${i + 1 + BUILT_IN_PAGES.length} · ${p.regions?.[0]?.emoji || ""} ${p.name} (fits ${pageCapacity({ ...p, trail: (p.trail || []).map((t) => (Array.isArray(t) ? t : [t.x, t.y])) })} chests, spaced out)`}
                  <br />
                  <button type="button" className="lb-btn lb-btn-soft" onClick={() => setEdit({ season: "", replaces: "", ...p, trail: (p.trail || []).map((t) => (Array.isArray(t) ? t : [t.x, t.y])), emoji: p.regions?.[0]?.emoji || "🗺️" })}>
                    Edit
                  </button>{" "}
                  <button type="button" className="lb-btn lb-btn-soft" onClick={() => move(p, -1)} disabled={i === 0}>
                    ◀
                  </button>{" "}
                  <button type="button" className="lb-btn lb-btn-soft" onClick={() => move(p, 1)} disabled={i === pages.length - 1}>
                    ▶
                  </button>{" "}
                  <button type="button" className="lb-btn lb-btn-soft" onClick={() => window.confirm(`Delete "${p.name}"?`) && deleteDoc(doc(db, "treasureMapPages", p.id))}>
                    Delete
                  </button>
                </figcaption>
              </figure>
            ))}
          </div>

          {!edit ? (
            <div>
              <button type="button" className="lb-btn" onClick={() => setEdit(blank())}>
                + Add a page
              </button>
              <p className="lb-hint" style={{ marginTop: 6 }}>
                Tip — make the picture in the Image Studio: “A hand-drawn treasure map page in a cheerful children's cartoon style on old parchment, with a dotted red trail from
                the left edge to the right edge through a waterfall valley (or a snowy village, a jungle, a volcano island…). Wide 16:9.”
              </p>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10, padding: 12, borderRadius: 14, background: "#fff8e6" }}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <label>
                  Land name{" "}
                  <input value={edit.name} onChange={(e) => set({ name: e.target.value })} placeholder="Waterfall Valley" />
                </label>
                <label>
                  Emoji <input value={edit.emoji} onChange={(e) => set({ emoji: e.target.value })} style={{ width: 50 }} />
                </label>
                <label>
                  Most chests on this page <input type="number" min="2" max="12" value={edit.capacity} onChange={(e) => set({ capacity: e.target.value })} style={{ width: 60 }} />
                </label>
                {(edit.trail || []).length >= 2 && (
                  <span className="lb-hint" title="Worked out from the trail: chests, their names and the avatar never overlap">
                    Room for {chestSlots(edit).length} chest{chestSlots(edit).length === 1 ? "" : "s"} along this trail
                    {Number(edit.capacity) > chestSlots(edit).length ? ` — only ${chestSlots(edit).length} will be used` : ""}
                  </span>
                )}
                <label>
                  <input type="checkbox" checked={edit.active !== false} onChange={(e) => set({ active: e.target.checked })} /> In use
                </label>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", padding: 8, borderRadius: 12, background: "#fff" }}>
                <label>
                  🎉 Holiday version of{" "}
                  <select value={edit.replaces || ""} onChange={(e) => set({ replaces: e.target.value })}>
                    <option value="">— no, a new page —</option>
                    {BUILT_IN_PAGES.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.regions?.[0]?.emoji} {b.name}
                      </option>
                    ))}
                  </select>
                </label>
                {edit.replaces && (
                  <>
                    <label>
                      during{" "}
                      <select value={edit.season || ""} onChange={(e) => set({ season: e.target.value })}>
                        <option value="">pick a season…</option>
                        {SEASONS.map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.emoji} {x.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    {edit.imageUrl && (
                      <button
                        type="button"
                        className="lb-btn lb-btn-soft"
                        onClick={() => set({ trail: (BUILT_IN_PAGES.find((b) => b.id === edit.replaces)?.trail || []).map((t) => [...t]) })}
                      >
                        Copy its trail
                      </button>
                    )}
                    <span className="lb-hint">Shown instead of that page while the season is on sale.</span>
                  </>
                )}
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <label className="lb-btn lb-btn-soft" style={{ cursor: "pointer" }}>
                  {busy ? "Uploading…" : "Upload a picture"}
                  <input type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} />
                </label>
                <span>or paste a link:</span>
                <input style={{ flex: 1, minWidth: 220 }} value={edit.imageUrl} onChange={(e) => set({ imageUrl: e.target.value.trim(), trail: [] })} placeholder="https://firebasestorage.googleapis.com/…" />
              </div>
              {edit.imageUrl && <TrailEditor page={edit} onChange={(patch) => set(patch)} />}
              <div>
                <button type="button" className="lb-btn" onClick={save} disabled={busy}>
                  Save page
                </button>{" "}
                <button type="button" className="lb-btn lb-btn-soft" onClick={() => setEdit(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
          {msg && <p className={`shp-msg is-${msg.tone}`}>{msg.text}</p>}
        </div>
      )}
    </section>
  );
};

export default TreasureMapPagesPanel;
